import React, { useState, useEffect } from 'react';
import { api } from '../../config/api';

const TagWordsModal = ({ isOpen, onClose, listId, wordIds, mode = 'add' }) => {
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTags();
    }
  }, [isOpen, listId]);

  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const data = await api.getTags(listId);
      setTags(data);
      setSelectedTags([]);
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas pobierania tagów');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTag = (tagId) => {
    setSelectedTags(prevSelected => {
      if (prevSelected.includes(tagId)) {
        return prevSelected.filter(id => id !== tagId);
      } else {
        return [...prevSelected, tagId];
      }
    });
  };

  const handleSubmit = async () => {
    if (selectedTags.length === 0) {
      setError('Wybierz przynajmniej jeden tag');
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === 'add') {
        // Dodawanie tagów do słów
        await Promise.all(
          wordIds.map(wordId => 
            Promise.all(
              selectedTags.map(tagId => 
                api.addTagToWord(wordId, tagId)
              )
            )
          )
        );
      } else {
        // Usuwanie tagów ze słów
        await Promise.all(
          wordIds.map(wordId => 
            Promise.all(
              selectedTags.map(tagId => 
                api.removeTagFromWord(wordId, tagId)
              )
            )
          )
        );
      }
      onClose(true); // Przekazujemy true, aby zasygnalizować sukces
    } catch (err) {
      setError(err.message || `Wystąpił błąd podczas ${mode === 'add' ? 'dodawania' : 'usuwania'} tagów`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
        <div className="p-6">
          <h2 className="text-sm font-semibold mb-4">
            {mode === 'add' ? 'Otaguj słówka' : 'Odtaguj słówka'}
          </h2>

          <button
            type="button"
            onClick={() => onClose(false)}
            className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {error && (
            <div className="mb-4 p-3 text-sm bg-red-50 text-red-600 rounded-md">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
            </div>
          ) : tags.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <p>Brak dostępnych tagów dla tej listy.</p>
              <p className="text-sm mt-2">Najpierw utwórz tagi w sekcji "Zarządzaj tagami".</p>
            </div>
          ) : (
            <div className="mb-6">
              <p className="text-xs font-medium text-neutral-700 mb-1">
                {mode === 'add' 
                  ? 'Wybierz tagi, które chcesz dodać do zaznaczonych słówek:' 
                  : 'Wybierz tagi, które chcesz usunąć z zaznaczonych słówek:'}
              </p>
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {tags.map(tag => (
                  <div
                    key={tag.id}
                    className={`flex items-center p-2 rounded-md cursor-pointer border ${
                      selectedTags.includes(tag.id)
                        ? 'bg-primary-50 border-primary-500'
                        : 'bg-white border-neutral-200 hover:bg-neutral-50'
                    }`}
                    onClick={() => handleToggleTag(tag.id)}
                  >
                    <div
                      className="w-4 h-4 rounded-full mr-2"
                      style={{ backgroundColor: tag.color }}
                    ></div>
                    <span className="text-xs">{tag.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => onClose(false)}
              className="px-3 py-1.5 text-xs font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors"
              disabled={isSubmitting}
            >
              Anuluj
            </button>
            <button
              onClick={handleSubmit}
              className={`px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors ${
                isSubmitting
                  ? 'bg-neutral-400 cursor-not-allowed'
                  : mode === 'add'
                    ? 'bg-primary-600 hover:bg-primary-700'
                    : 'bg-red-600 hover:bg-red-700'
              }`}
              disabled={isSubmitting || selectedTags.length === 0}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Przetwarzanie...
                </span>
              ) : (
                mode === 'add' ? 'Otaguj' : 'Odtaguj'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagWordsModal; 